<template>
  <base-drawer :show="show" bare width="720px" @close="emit('close')">
    <div class="lc">
      <div class="lc__top">
        <base-input v-if="overlays.length > 0" class="lc__select mb-0">
          <el-select v-model="selectedOverlayId" class="select-primary" placeholder="No overlay" clearable @change="renderOverlay">
            <el-option v-for="o in overlays" :key="'lc-' + o.id" class="select-primary"
                       :label="o.name + ' [' + o.aspect_ratio + ']'" :value="o.id"></el-option>
          </el-select>
        </base-input>
        <span v-else class="lc__spacer"></span>
        <button type="button" class="base-drawer__header-btn lc__close" aria-label="Close" @click="emit('close')">
          <i class="tim-icons icon-simple-remove"></i>
        </button>
      </div>

      <div ref="stage" class="lc__stage">
        <video ref="videoEl" class="lc__video" :class="{ 'lc__video--mirror': facing === 'user' }" autoplay playsinline muted></video>
        <canvas ref="overlayEl" class="lc__overlay"></canvas>
        <div v-if="error" class="lc__error">
          <p class="text-white">{{ error }}</p>
          <base-button type="primary" size="sm" @click="emit('use-native')">Use system camera</base-button>
        </div>
        <p v-else-if="!ready" class="lc__loading text-white">Starting camera</p>
      </div>

      <div class="lc__bottom">
        <button type="button" class="lc__ghost" :disabled="!ready || capturing" aria-label="Switch camera" @click="flip">
          <i class="tim-icons icon-refresh-02"></i>
        </button>
        <button type="button" class="lc__shutter" :disabled="!ready || capturing" aria-label="Take photo" @click="capture"></button>
        <button type="button" class="lc__ghost lc__ghost--text" :disabled="capturing" @click="emit('use-native')">Use system camera</button>
      </div>
    </div>
  </base-drawer>
</template>

<script setup lang="ts">
// Live overlay camera. The browser's camera stream shows inside the console
// with the chosen overlay drawn over it at the same contain fit the bake uses,
// so staff frame the group around the overlay before the shutter. The captured
// frame is a clean JPEG at stream resolution; the page bakes the overlay through
// its normal path, which lands it exactly where the preview showed it.
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { fitTransform, effectiveLayers, designSizeFor, cropsToOverlay, artboardRect } from '@/util/overlayGeometry'
import { renderOverlayStage } from '@/util/overlayRenderer'

interface OverlayRow {
  id: number
  name: string
  layers: string | any[]
  aspect_ratio: string
  reference_width: number
  reference_height: number
  crop_to_overlay?: number | boolean
}

const props = withDefaults(defineProps<{
  show: boolean
  overlays?: OverlayRow[]
  overlayId?: number | null
  resolveText?: (content: string) => Promise<string> | string
}>(), {
  overlays: () => [],
  overlayId: null,
  resolveText: undefined,
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'capture', payload: { file: File; overlayId: number | null }): void
  (e: 'use-native'): void
}>()

const stage = ref<HTMLElement | null>(null)
const videoEl = ref<HTMLVideoElement | null>(null)
const overlayEl = ref<HTMLCanvasElement | null>(null)
const selectedOverlayId = ref<number | ''>('')
const facing = ref<'environment' | 'user'>('environment')
const ready = ref(false)
const capturing = ref(false)
const error = ref('')

let stream: MediaStream | null = null
// The overlay rendered over the whole video frame (in design units), like the
// bake does, so text that runs past the designed area is not clipped. `aspect`
// is the video aspect it was rendered for; a rotation re-renders it. With crop
// on, the stage is the artboard only and layout() dims what the bake cuts away.
let stageImage: { canvas: HTMLCanvasElement; aspect: number | null; crop: boolean } | null = null
let renderToken = 0

const selectedOverlay = computed<OverlayRow | null>(() => {
  if (selectedOverlayId.value === '') return null
  return props.overlays.find(o => o.id === selectedOverlayId.value) || null
})

async function start() {
  error.value = ''
  stop()
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    error.value = 'This browser cannot share its camera.'
    return
  }
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: facing.value }, width: { ideal: 4096 }, height: { ideal: 2160 } },
      audio: false,
    })
  } catch (e: any) {
    const name = e && e.name
    error.value = name === 'NotAllowedError' || name === 'SecurityError'
      ? 'Camera access was blocked. Allow it in the browser settings, or use the system camera.'
      : 'The camera could not be started.'
    return
  }
  const video = videoEl.value
  if (!video || !props.show) {
    stop()
    return
  }
  video.srcObject = stream
  video.onloadedmetadata = () => {
    ready.value = true
    layout()
  }
  // The stream's dimensions change after a rotation, later than the window
  // resize event; the video element reports it with its own resize event.
  video.onresize = () => layout()
  try { await video.play() } catch (e) { /* autoplay refused; the first tap on the shutter plays */ }
}

function stop() {
  if (stream) {
    stream.getTracks().forEach(t => t.stop())
    stream = null
  }
  if (videoEl.value) {
    videoEl.value.onresize = null
    videoEl.value.srcObject = null
  }
  ready.value = false
}

function flip() {
  facing.value = facing.value === 'environment' ? 'user' : 'environment'
  start()
}

async function renderOverlay() {
  const token = ++renderToken
  const overlay = selectedOverlay.value
  if (!overlay) {
    stageImage = null
    layout()
    return
  }
  const eff = effectiveLayers(overlay, null)
  const video = videoEl.value
  const vw = video ? video.videoWidth : 0
  const vh = video ? video.videoHeight : 0
  let frame: { width: number; height: number } | null = null
  let offset: { x: number; y: number } | null = null
  let aspect: number | null = null
  const crop = cropsToOverlay(overlay)
  if (vw && vh) {
    aspect = vw / vh
    // Crop on: the stage is the artboard only, so a layer past the designed
    // area is cut off here exactly as the bake cuts it.
    if (!crop) {
      const design = designSizeFor(overlay.aspect_ratio)
      const fit = fitTransform(design, vw, vh)
      frame = { width: vw / fit.scale, height: vh / fit.scale }
      offset = { x: fit.offsetX / fit.scale, y: fit.offsetY / fit.scale }
    }
  }
  const image = await renderOverlayStage({
    layers: eff.layers,
    aspectRatio: overlay.aspect_ratio,
    referenceWidth: eff.referenceWidth,
    referenceHeight: eff.referenceHeight,
    resolveText: props.resolveText,
    frame,
    offset,
  })
  if (token !== renderToken) return
  stageImage = { canvas: image.canvas, aspect, crop }
  layout()
}

// Size the overlay canvas to the displayed video rectangle (contain fit inside
// the stage box) and draw the stage image with the bake's fit transform.
function layout() {
  const video = videoEl.value
  const canvas = overlayEl.value
  const box = stage.value
  if (!video || !canvas || !box) return
  const vw = video.videoWidth
  const vh = video.videoHeight
  const bw = box.clientWidth
  const bh = box.clientHeight
  if (!vw || !vh || !bw || !bh) return
  const s = Math.min(bw / vw, bh / vh)
  const rw = Math.round(vw * s)
  const rh = Math.round(vh * s)
  const dpr = window.devicePixelRatio || 1
  canvas.style.width = rw + 'px'
  canvas.style.height = rh + 'px'
  canvas.style.left = Math.round((bw - rw) / 2) + 'px'
  canvas.style.top = Math.round((bh - rh) / 2) + 'px'
  canvas.width = Math.round(rw * dpr)
  canvas.height = Math.round(rh * dpr)
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (!stageImage) return
  // Rendered for another aspect (or before the stream had dimensions): re-render.
  const aspect = vw / vh
  if (stageImage.aspect === null || Math.abs(stageImage.aspect - aspect) > 0.01) {
    renderOverlay()
    return
  }
  if (!stageImage.crop || !selectedOverlay.value) {
    ctx.drawImage(stageImage.canvas, 0, 0, canvas.width, canvas.height)
    return
  }
  // Crop on: the overlay covers its own area and everything the bake cuts away is dimmed.
  const design = designSizeFor(selectedOverlay.value.aspect_ratio)
  const k = canvas.width / vw
  const art = artboardRect(design, vw, vh)
  const ax = art.x * k
  const ay = art.y * k
  const aw = art.width * k
  const ah = art.height * k
  ctx.drawImage(stageImage.canvas, ax, ay, aw, ah)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
  ctx.beginPath()
  ctx.rect(0, 0, canvas.width, canvas.height)
  ctx.rect(ax, ay, aw, ah)
  ctx.fill('evenodd')
}

function capture() {
  const video = videoEl.value
  if (!video || !ready.value || capturing.value) return
  capturing.value = true
  const canvas = document.createElement('canvas')
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    capturing.value = false
    return
  }
  // Drawn as the sensor sees it: the front camera preview is mirrored on screen only.
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
  canvas.toBlob((blob) => {
    capturing.value = false
    if (!blob) return
    const file = new File([blob], 'live-' + Date.now() + '.jpg', { type: 'image/jpeg' })
    emit('capture', { file, overlayId: selectedOverlayId.value === '' ? null : selectedOverlayId.value })
  }, 'image/jpeg', 0.92)
}

function onKeydown(e: KeyboardEvent) {
  if (!props.show) return
  if (e.key === 'Escape') emit('close')
}

function onResize() {
  layout()
}

// On a rotation Android keeps the stream in the old orientation until it is
// renegotiated, which leaves a landscape band in a portrait box. Restart the
// stream once the window has settled in the new orientation.
let orientationTimer: ReturnType<typeof setTimeout> | null = null
function onOrientationChange() {
  if (orientationTimer) clearTimeout(orientationTimer)
  orientationTimer = setTimeout(() => {
    orientationTimer = null
    if (props.show && !error.value) start()
  }, 350)
}

watch(() => props.show, async (open) => {
  if (open) {
    selectedOverlayId.value = typeof props.overlayId === 'number' ? props.overlayId : ''
    facing.value = 'environment'
    document.addEventListener('keydown', onKeydown)
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onOrientationChange)
    await nextTick()
    start()
    renderOverlay()
  } else {
    document.removeEventListener('keydown', onKeydown)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('orientationchange', onOrientationChange)
    if (orientationTimer) clearTimeout(orientationTimer)
    stop()
    stageImage = null
    error.value = ''
    capturing.value = false
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', onResize)
  window.removeEventListener('orientationchange', onOrientationChange)
  if (orientationTimer) clearTimeout(orientationTimer)
  stop()
})
</script>

<style scoped>
/* Camera UI: dark in both themes on purpose. */
.lc {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #000;
  color: #fff;
}
.lc__top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 8px;
  padding-top: calc(12px + env(safe-area-inset-top, 0px));
}
.lc__select { flex: 1; min-width: 0; }
.lc__spacer { flex: 1; }
.lc__close { flex: none; }
.lc__stage {
  flex: 1;
  position: relative;
  min-height: 0;
  overflow: hidden;
}
.lc__video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
}
.lc__video--mirror { transform: scaleX(-1); }
.lc__overlay {
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
}
.lc__error,
.lc__loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 0;
  padding: 24px;
  text-align: center;
  font-size: 13px;
}
.lc__loading { opacity: 0.7; }
.lc__bottom {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 14px 16px;
  padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px));
}
.lc__shutter {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 4px solid #fff;
  background: transparent;
  padding: 0;
  cursor: pointer;
  justify-self: center;
}
.lc__shutter:active { background: rgba(255, 255, 255, 0.35); }
.lc__shutter:disabled { opacity: 0.4; cursor: default; }
.lc__ghost {
  background: transparent;
  border: none;
  color: #fff;
  opacity: 0.8;
  font-size: 20px;
  padding: 8px;
  cursor: pointer;
  justify-self: start;
}
.lc__ghost--text {
  font-size: 12px;
  text-decoration: underline;
  justify-self: end;
  text-align: right;
}
.lc__ghost:disabled { opacity: 0.35; cursor: default; }
</style>
